import React, { useCallback, useState } from "react";
import { useRespondedQuoteRequests } from "../../application/hooks/useRespondedQuoteRequests";
import { useRespondedQuoteFilters } from "../../application/hooks/useRespondedQuoteFilters";
import {
  Skeleton,
  ProjectCardHorizontal,
  ProjectCard,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@semo/shared-ui";
import {
  Package,
  AlertCircle,
  MessageCircle,
  List as ListIcon,
  LayoutGrid,
} from "@semo/icons";
import { cn } from "@semo/shared-ui";
import { useChatControllerGetRooms } from "@semo/api-contracts";
import type { QuoteRequestListItemDto } from "@semo/api-contracts";
import { RespondedProjectDetailPage } from "../pages/RespondedProjectDetailPage";
import { toast } from "sonner";

interface RespondedProjectListProps {
  onOpenChat: (roomId: string) => void;
  /** DEV 전용: 실제 API 대신 샘플 참여 공고로 렌더 (/dev/preview). */
  previewProjects?: QuoteRequestListItemDto[];
}

// 상태 세그먼트 탭 (프로젝트 찾기와 동일 스타일)
const RESPONDED_TABS = [
  { key: "all", label: "전체" },
  { key: "IN_PROGRESS", label: "진행중" },
  { key: "CLOSED", label: "마감" },
] as const;

export const RespondedProjectList: React.FC<RespondedProjectListProps> = ({
  onOpenChat,
  previewProjects,
}) => {
  const isPreview = previewProjects !== undefined;
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(
    null,
  );
  const [view, setView] = useState<"list" | "card">("card");
  const { data, isLoading, error, refetch } = useRespondedQuoteRequests({
    page: 1,
    limit: 100,
  });

  // Pre-fetch chat rooms for linking
  const { data: chatRoomsData } = useChatControllerGetRooms({
    page: 1,
    limit: 100,
  });

  const requests = previewProjects ?? data?.data?.items ?? [];

  // 필터링 로직 (ContractListPage와 동일한 패턴)
  const { activeFilter, setActiveFilter, filteredRequests, counts } =
    useRespondedQuoteFilters(requests);

  const handleProjectClick = useCallback(
    (requestId: string) => {
      // 미리보기: 상세 모달은 실데이터가 필요해 열지 않는다 (카드 레이아웃 확인용)
      if (isPreview) return;
      setSelectedRequestId(requestId);
    },
    [isPreview],
  );

  const handleChatClick = useCallback(
    (quoteRequestId: string) => {
      if (isPreview) {
        // 미리보기: 채팅 탭(마스터-디테일 목업)으로 이동 + 해당 방 선택
        onOpenChat(quoteRequestId);
        return;
      }
      const rooms = chatRoomsData?.data?.items ?? [];
      const targetRoom = rooms.find((r) => r.quoteRequestId === quoteRequestId);

      if (targetRoom) {
        onOpenChat(targetRoom.id);
      } else {
        toast.error("연결된 채팅방을 찾을 수 없습니다.");
      }
    },
    [isPreview, chatRoomsData, onOpenChat],
  );

  const renderChatAction = (id: string) => (
    <button
      onClick={(e) => {
        e.stopPropagation();
        handleChatClick(id);
      }}
      className="px-4 py-2 bg-primary/5 text-primary border border-primary/20 rounded-lg text-sm font-semibold hover:bg-primary/10 transition-colors flex items-center justify-center gap-2"
    >
      <span>채팅하기</span>
      <MessageCircle className="w-4 h-4" />
    </button>
  );

  if (!isPreview && isLoading) {
    return (
      <div className="p-4 sm:p-6 md:p-8">
        <div className="flex flex-wrap items-center gap-3 mb-8">
          <Skeleton className="h-[32px] w-[80px]" />
          <Skeleton className="h-[32px] w-[100px]" />
          <Skeleton className="h-[32px] w-[80px]" />
        </div>
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="h-28 bg-slate-50 animate-pulse rounded-2xl w-full border border-slate-100"
            />
          ))}
        </div>
      </div>
    );
  }

  if (!isPreview && error) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4 text-center">
          <AlertCircle className="w-12 h-12 text-red-500" />
          <p className="text-slate-600 ">
            프로젝트를 불러오는 중 오류가 발생했습니다.
          </p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary-hover transition-colors"
          >
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="p-4 sm:p-6 md:p-8 h-full overflow-y-auto">
        {/* 헤딩 + 설명 — 프로젝트 찾기와 톤 통일 */}
        <div className="mb-4">
          <h1 className="text-xl font-semibold tracking-tight text-slate-900">
            나의 프로젝트
          </h1>
          <p className="mt-1 text-sm leading-relaxed text-slate-500">
            내가 참여한 프로젝트를 관리하세요.
          </p>
        </div>

        {/* 상태 세그먼트 탭 + 리스트/카드 토글 */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="inline-flex items-center gap-1 rounded-xl bg-slate-100 p-1">
            {RESPONDED_TABS.map((tab) => {
              const active = activeFilter === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setActiveFilter(tab.key)}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold transition-all",
                    active
                      ? "bg-white text-primary shadow-sm"
                      : "text-slate-500 hover:text-slate-700",
                  )}
                >
                  {tab.label}
                  <span
                    className={cn(
                      "rounded-full px-1.5 py-0.5 text-2xs font-semibold leading-none",
                      active
                        ? "bg-primary/10 text-primary"
                        : "bg-slate-200/70 text-slate-500",
                    )}
                  >
                    {counts[tab.key]}
                  </span>
                </button>
              );
            })}
          </div>

          {/* 카드 / 리스트 토글 (아이콘 + 글씨) */}
          <div className="inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-white p-1">
            <button
              type="button"
              aria-pressed={view === "card"}
              onClick={() => setView("card")}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold transition-all",
                view === "card"
                  ? "bg-primary/10 text-primary"
                  : "text-slate-500 hover:text-slate-700",
              )}
            >
              <LayoutGrid className="h-4 w-4" />
              카드
            </button>
            <button
              type="button"
              aria-pressed={view === "list"}
              onClick={() => setView("list")}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold transition-all",
                view === "list"
                  ? "bg-primary/10 text-primary"
                  : "text-slate-500 hover:text-slate-700",
              )}
            >
              <ListIcon className="h-4 w-4" />
              리스트
            </button>
          </div>
        </div>

        {/* 프로젝트 목록 — 리스트/카드 토글 */}
        {filteredRequests.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Package className="w-16 h-16 text-slate-300  mb-4" />
            <p className="text-slate-500  text-lg">
              참여한 프로젝트가 없습니다.
            </p>
          </div>
        ) : view === "list" ? (
          <div className="space-y-4">
            {filteredRequests.map((item) => (
              <ProjectCardHorizontal
                key={item.id}
                project={item}
                onClick={() => handleProjectClick(item.id)}
                action={renderChatAction(item.id)}
              />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:gap-5 md:gap-6 md:grid-cols-2 xl:grid-cols-3">
            {filteredRequests.map((item) => (
              <ProjectCard
                key={item.id}
                project={item}
                onClick={() => handleProjectClick(item.id)}
                action={renderChatAction(item.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      <Dialog
        open={!!selectedRequestId}
        onOpenChange={(open) => !open && setSelectedRequestId(null)}
      >
        <DialogContent
          size="3xl"
          className="p-0 overflow-hidden h-[90vh]"
          aria-describedby={undefined}
        >
          <DialogTitle className="sr-only">프로젝트 상세</DialogTitle>
          {selectedRequestId && (
            <RespondedProjectDetailPage
              requestId={selectedRequestId}
              onBack={() => setSelectedRequestId(null)}
              onChatClick={handleChatClick}
              hideBuyerInfo={false}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
